import React, { useState } from 'react';
import { Archive, ArchiveRestore, Loader2, X } from 'lucide-react';
import Modal from '../../components/Modal';
import { useToast } from '../../components/ToastProvider';
import { bulkArchiveRequests, bulkUnarchiveRequests } from './requestBulkApi';

// Sticky bar shown while request rows are selected: count + the one bulk
// operation this view offers. Which one it is follows the list's Archived
// toggle — you archive from the live list and unarchive from the archived one,
// so there is never an action that would empty the view you are not looking at.
// Selection is page-scoped; the caller clears it and refreshes after apply.
export default function RequestsBulkActionsBar({
  selectedIds,
  archivedView = false,
  onClearSelection,
  onApplied,
}) {
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const count = selectedIds.size;
  if (count === 0) return null;

  const title = archivedView ? 'Unarchive requests' : 'Archive requests';
  const noun = count === 1 ? 'request' : 'requests';
  const Icon = archivedView ? ArchiveRestore : Archive;

  const closeModal = () => {
    if (busy) return;
    setConfirming(false);
  };

  const apply = async () => {
    const ids = [...selectedIds];
    setBusy(true);
    try {
      const result = archivedView
        ? await bulkUnarchiveRequests(ids)
        : await bulkArchiveRequests(ids);

      const unchanged = (result.results || []).filter((r) => r.ok && !r.changed).length;
      const parts = [`${result.updated} ${archivedView ? 'unarchived' : 'archived'}`];
      if (unchanged > 0) parts.push(`${unchanged} already ${archivedView ? 'active' : 'archived'}`);
      if (result.failed > 0) parts.push(`${result.failed} not found`);
      const summary = `${parts.join(', ')}.`;
      if (result.failed > 0) toast.warning(`${title} — partial`, summary);
      else toast.success(title, summary);
      setConfirming(false);
      onApplied();
    } catch (err) {
      toast.error('Bulk update failed', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="bulk-bar animate-fade-in" role="toolbar" aria-label="Bulk actions">
        <span className="bulk-bar-count">
          {count} {noun} selected
        </span>
        <div className="bulk-bar-actions">
          <button type="button" className="btn outline sm" onClick={() => setConfirming(true)}>
            <Icon className="w-3.5 h-3.5" />
            {archivedView ? 'Unarchive' : 'Archive'}
          </button>
        </div>
        <button type="button" className="btn ghost sm" onClick={onClearSelection}>
          <X className="w-3.5 h-3.5" />
          Clear
        </button>
      </div>

      <Modal
        isOpen={confirming}
        onClose={closeModal}
        title={title}
        description={`Applies to the ${count} selected ${noun}.`}
        size="sm"
        footer={(
          <>
            <button type="button" onClick={closeModal} className="btn outline sm" disabled={busy}>
              Cancel
            </button>
            <button type="button" onClick={apply} className="btn primary sm" disabled={busy}>
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {archivedView ? 'Unarchive' : 'Archive'}
            </button>
          </>
        )}
      >
        <div className="card-body">
          <div className="stack-sm" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {archivedView ? (
              <div>
                Returns the selected {noun} to the active list. Nothing else about them changes —
                status, reviews and documents are untouched.
              </div>
            ) : (
              <div>
                Hides the selected {noun} from the default list. They stay readable under the
                Archived view and can be unarchived at any time.
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
